import { API_BASE_URL } from "./env";
import { formatErrorDetail } from "./errors";

const API_BASE = API_BASE_URL;

export interface AuthTokens {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in?: number;
}

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  organization_id: string;
}

export interface LoginResult {
  tokens?: AuthTokens;
  challenge?: string;
  session?: string;
  message?: string;
}

const TOKEN_KEY = "criterion_tokens";

export function getStoredTokens(): AuthTokens | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function storeTokens(tokens: AuthTokens): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const resp = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(formatErrorDetail(err.detail, "Login failed"));
  }

  const data = await resp.json();

  if (data.challenge === "NEW_PASSWORD_REQUIRED") {
    return { challenge: data.challenge, session: data.session, message: data.message };
  }

  const tokens: AuthTokens = {
    access_token: data.access_token,
    id_token: data.id_token,
    refresh_token: data.refresh_token,
    expires_in: data.expires_in,
  };
  storeTokens(tokens);
  return { tokens };
}

export async function setNewPassword(email: string, newPassword: string, session: string): Promise<AuthTokens> {
  const resp = await fetch(`${API_BASE}/auth/set-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, new_password: newPassword, session }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(formatErrorDetail(err.detail, "Password change failed"));
  }

  const data = await resp.json();
  const tokens: AuthTokens = {
    access_token: data.access_token,
    id_token: data.id_token,
    refresh_token: data.refresh_token,
    expires_in: data.expires_in,
  };
  storeTokens(tokens);
  return tokens;
}

export async function refreshSession(): Promise<AuthTokens | null> {
  const stored = getStoredTokens();
  if (!stored?.refresh_token) return null;

  try {
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: stored.refresh_token }),
    });

    if (!resp.ok) {
      clearTokens();
      return null;
    }

    const data = await resp.json();
    const tokens: AuthTokens = {
      access_token: data.access_token,
      id_token: data.id_token,
      refresh_token: stored.refresh_token,
      expires_in: data.expires_in,
    };
    storeTokens(tokens);
    return tokens;
  } catch {
    clearTokens();
    return null;
  }
}

export async function fetchMe(token: string): Promise<AuthUser> {
  const resp = await fetch(`${API_BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) throw new Error("Failed to fetch user");
  return resp.json();
}

export function logout(): void {
  clearTokens();
  window.location.href = "/login";
}
